import React, { PropsWithChildren, useEffect } from "react";
import { useChatMessages } from "../../../adapters/cla/state/hooks/stateHooks.ts";
import { MessageBubble } from "../../../feature-components/LegacyChatMessages/MessageBubble.tsx";
import { motion } from "motion/react";
import styles from "../../../ui-library/MarkdownContent.module.css";
import Markdown from "react-markdown";
import { LoadingDots } from "../../../ui-library/LoadingDots/LoadingDots.tsx";
import remarkGfm from "remark-gfm";
import { ExecutionDetails } from "../../../feature-components/ExecutionDetails/ExecutionDetails.tsx";
import type { Message } from "../../../adapters/cla/state/ClaState.ts";
import { MessageRenderer } from "./MessageRenderer.tsx";
import { useFeatureFlagValue } from "../../../config/FeatureFlagManager/hooks";
import { MessageContext } from "../../../feature-components/LegacyChatMessages/MessageContext.tsx";
import { useClaService } from "../../../adapters/cla/state/hooks/useClaService.ts";
import { agentNames } from "./agentNames.ts";

export const ClaConnectedChatMessages: React.FC<{ chatId: string }> = (
  props,
) => {
  const { messages, loading } = useChatMessages(props.chatId);
  const limitedHeightExecutionDetailsFlag = useFeatureFlagValue(
    "enableLimitedHeightExecutionDetails",
  );

  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length]);

  return (
    <motion.div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        overflowY: "auto",
        padding: "8px",
        boxSizing: "border-box",
        flexGrow: 1,
      }}
      initial={{ flexGrow: 0 }}
      animate={{ flexGrow: 1 }}
      transition={{ duration: 0.1, ease: "easeInOut" }}
    >
      {loading && messages.length === 0 && <div>Loading messages...</div>}
      {messages.map((message) => (
        <MessageComponent
          key={message.id}
          message={message}
          flags={{
            limitedHeightExecutionDetailsFlag,
          }}
          chatId={props.chatId}
        />
      ))}
    </motion.div>
  );
};

const MessageComponent = (props: {
  message: Message;
  flags: { limitedHeightExecutionDetailsFlag: boolean };
  chatId: string;
}) => {
  const [showThoughts, setShowThoughts] = React.useState(true);

  const toggleThoughts = () => {
    setShowThoughts((show) => !show);
  };

  useEffect(() => {
    if (props.message.status === "completed") {
      setShowThoughts(false);
    }
  }, [props.message.status]);

  const agentName =
    props.message.agentName && agentNames[props.message.agentName];
  return (
    <MessageProvider chatId={props.chatId} messageId={props.message.id}>
      <MessageBubble
        key={props.message.id}
        message={{
          value: props.message.content,
          sender: props.message.sender,
          create_time: props.message.createTime.toString(),
          id: props.message.id,
          status: "completed",
        }}
      >
        {(props.message.thoughts || agentName) && (
          <ExecutionDetails
            open={showThoughts}
            onToggle={toggleThoughts}
            enableLimitedHeight={
              !!(
                props.message.thoughts?.length &&
                // Arbitrary length to determine if we should limit height
                // Otherwise, the gradient covers the agent title
                props.message.thoughts?.length > 200 &&
                props.flags.limitedHeightExecutionDetailsFlag
              )
            }
            title={
              props.message.status === "processing" ? (
                <>
                  {agentName || "Thinking"} <LoadingDots />
                </>
              ) : (
                "Execution Details"
              )
            }
          >
            <div className={styles.MarkdownContent}>
              <Markdown remarkPlugins={[remarkGfm]}>
                {props.message.thoughts}
              </Markdown>
            </div>
          </ExecutionDetails>
        )}
        <div className={styles.MarkdownContent}>
          <MessageRenderer message={props.message} chatId={props.chatId} />
        </div>
        {props.message.id === "optimistic-agent-msg" &&
          props.message.sender === "agent" && (
            <span>
              Thinking <LoadingDots />
            </span>
          )}
      </MessageBubble>
    </MessageProvider>
  );
};

const MessageProvider: React.FC<
  PropsWithChildren<{ chatId: string; messageId: string }>
> = ({ children, chatId, messageId }) => {
  const claService = useClaService();
  const enableRetryLastQuery = useFeatureFlagValue("enableRetryLastQuery");
  const isLastMessageFromAgent =
    claService.getLastMessage(chatId, "agent")?.id === messageId;
  const isLastMessageFromUser =
    claService.getLastMessage(chatId, "user")?.id === messageId;
  const shouldDisplayRetryButton =
    (isLastMessageFromAgent || isLastMessageFromUser) && enableRetryLastQuery;

  const messageContextValue = React.useMemo(() => {
    return {
      resendLastQuery: shouldDisplayRetryButton
        ? () => claService.resendLastQuery({ chatId })
        : undefined,
    };
  }, [claService, chatId, shouldDisplayRetryButton]);
  return (
    <MessageContext.Provider value={messageContextValue}>
      {children}
    </MessageContext.Provider>
  );
};
